import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { FolderType } from 'services/folders.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import {
  fetchProjects,
  removeProject,
  removeFinalcut,
  removeStory,
  fetchFinalcuts,
  fetchStories,
  setStoryWithFinalcut,
  setRemovingInstance,
  fetchStorageSize,
  removeFolder,
  fetchFolders,
} from 'components/app/_mediator/app-data.slice';
import {
  clearHeldByMediaFileId,
  removeUploadingFile,
} from 'components/app/_mediator/app-files-upload.slice';
import {
  setSelectedFile,
  setSelectedProject,
  setSelectedFinalcut,
  setSelectedStory,
  setSelectedFolder,
} from 'components/app/projects/_mediator/projects.slice';
import { resetBackup } from 'components/app/editor/_mediator/backup/backup.slice';
import { cleanDeletedClipsByProjectId } from 'components/app/editor/_mediator/editor.slice';
import { removeFile, fetchFiles } from 'components/app/_mediator/app-files.slice';
import { useProjectsSearch } from 'components/app/projects/use-projects-search';
import { UNFILED_FOLDER_ID } from 'components/app/projects/_mediator/projects.params';

import { Modal } from 'components/UI/modal/modal';
import { Button } from 'components/UI/button/button';
import { Loader } from 'components/UI/loader/loader';

import styles from './_project-deletions-modal.module.scss';

const INSTANCE_LABELS = {
  finalcut: 'Final Cut',
  project: 'project',
  story: 'story',
  folder: 'folder',
  file: 'file',
} as const;

export const ProjectDeletionsModal = () => {
  const storiesFilter = useProjectsSearch('storiesSearchFilter');
  const foldersFilter = useProjectsSearch('foldersSearchFilter');
  const { getFilteredList } = useProjectsSearch('projectsSearchFilter');
  const { projects, removingInstance, stories, finalcuts, folders } = useAppSelector((state) => state.app__data);
  const files = useAppSelector((state) => state.app__files.files);
  const uploadingFiles = useAppSelector((state) => state.app__filesUpload.uploadingFiles);
  const { selectedFinalcut, selectedStory, selectedProject, selectedFolder } = useAppSelector(
    (state) => state.projects
  );
  const [removing, setRemoving] = useState(false);
  const dispatch = useAppDispatch();

  const instanceType = removingInstance?.instance ?? null;

  const label = instanceType ? INSTANCE_LABELS[instanceType] : 'item';
  const modalTitle = `Are you sure you want to delete this ${label}?`;

  const instanceName = useMemo(() => {
    if (!removingInstance) return null;
    const { instance, id } = removingInstance;
    switch (instance) {
      case 'finalcut':
        return finalcuts.find((fc) => fc.id === id)?.name ?? null;
      case 'project':
        return projects?.find((project) => project.projectId === id)?.name ?? null;
      case 'story':
        return stories?.find((story) => story.storyId === id)?.name ?? null;
      case 'folder':
        return folders?.find((folder) => folder.id === id)?.name ?? null;
      case 'file':
        return typeof id === 'string'
          ? uploadingFiles.find((file) => file.id === id)?.fileName ?? null
          : files[id]?.fileName ?? null;
    }
  }, [removingInstance, finalcuts, projects, stories, folders, files, uploadingFiles]);

  const handleRemoveFolder = async () => {
    if (!selectedFolder || !folders) return;
    try {
      setRemoving(true);
      const { getFilteredList } = foldersFilter;
      await dispatch(removeFolder(removingInstance!.id as number));
      const result = await dispatch(fetchFolders()).unwrap();
      const filtered = getFilteredList(result.filter((el) => el.type === FolderType.Custom));
      dispatch(fetchStorageSize());
      if (filtered.length) {
        const i = getFilteredList(folders.filter((el) => el.type === FolderType.Custom)).findIndex(
          (el) => el.id === removingInstance!.id
        );
        const index = i < filtered.length ? i : i - 1;
        dispatch(setRemovingInstance({ id: filtered[index].id, instance: 'folder', modalOpen: false }));
        dispatch(setSelectedFolder(filtered[index].id ?? UNFILED_FOLDER_ID));
      } else {
        dispatch(setRemovingInstance(null));
        dispatch(setSelectedFolder(UNFILED_FOLDER_ID));
      }
    } catch (err) {
      dispatch(setRemovingInstance(null));
    }
    setRemoving(false);
  };

  const handleRemoveProject = async () => {
    if (!selectedProject || !projects) return;
    try {
      setRemoving(true);
      await dispatch(removeProject(removingInstance!.id as number));
      const result = await dispatch(fetchProjects()).unwrap();
      const filtered = getFilteredList(result);
      dispatch(fetchStorageSize());
      if (filtered.length) {
        const i = getFilteredList(projects).findIndex((el) => el.projectId === removingInstance!.id);
        const index = i < filtered.length ? i : i - 1;
        dispatch(setRemovingInstance({ id: filtered[index].projectId, instance: 'project', modalOpen: false }));
        dispatch(setSelectedProject(filtered[index].projectId));
      } else {
        dispatch(setRemovingInstance(null));
        dispatch(setSelectedProject(null));
      }
    } catch (err) {
      dispatch(setRemovingInstance(null));
    }
    setRemoving(false);
  };

  const handleRemoveFile = async () => {
    if (typeof removingInstance?.id === 'string') {
      dispatch(removeUploadingFile(removingInstance!.id as string));
      dispatch(setSelectedFile(null));
      dispatch(setRemovingInstance(null));
      return;
    }

    if (!selectedProject) return;

    setRemoving(true);

    if (stories && stories.length) {
      await dispatch(cleanDeletedClipsByProjectId(selectedProject));
      dispatch(resetBackup());
    }
    const mediaFileId = removingInstance!.id as number;
    dispatch(removeFile(mediaFileId))
      .unwrap()
      .then(() => {
        clearHeldByMediaFileId(mediaFileId, dispatch);
        dispatch(fetchFiles(selectedProject));
        dispatch(fetchProjects());
        dispatch(fetchStorageSize());
        dispatch(setSelectedFile(null));
      })
      .finally(() => {
        dispatch(setRemovingInstance(null));
        setRemoving(false);
      });
  };

  const handleRemoveStory = async () => {
    if (!selectedStory || !selectedProject || !stories) return;
    if (finalcuts.length) {
      toast.error('You cannot delete this story because it includes Final Cuts. Please remove them first to proceed.');
      dispatch(setRemovingInstance(null));
      return;
    }
    try {
      setRemoving(true);
      await dispatch(removeStory(removingInstance!.id as number));
      const result = await dispatch(fetchStories(selectedProject)).unwrap();
      const { getFilteredList } = storiesFilter;
      const filtered = getFilteredList(result);
      if (filtered.length) {
        const i = getFilteredList(stories).findIndex((el) => el.storyId === removingInstance!.id);
        const index = i < filtered.length ? i : i - 1;
        dispatch(setRemovingInstance({ id: filtered[index].storyId, instance: 'story', modalOpen: false }));
        dispatch(setSelectedStory(filtered[index].storyId));
      } else {
        dispatch(setRemovingInstance(null));
        dispatch(setSelectedStory(null));
      }
    } catch (err) {
      dispatch(setRemovingInstance(null));
    }
    setRemoving(false);
  };

  const handleRemoveFinalcut = async () => {
    if (!selectedStory || !selectedProject) return;
    try {
      setRemoving(true);
      await dispatch(removeFinalcut(removingInstance!.id as number));
      await dispatch(fetchFinalcuts(selectedStory))
        .unwrap()
        .then((resFinalcuts) => {
          if (removingInstance!.id === selectedFinalcut) {
            if (resFinalcuts.length === 0) {
              dispatch(setSelectedFinalcut(null));
              dispatch(setStoryWithFinalcut({ storyId: selectedStory, withFinalcuts: false }));
            } else {
              const i = finalcuts.findIndex((el) => el.id === selectedFinalcut);
              const index = i < resFinalcuts.length ? i : i - 1;
              dispatch(setSelectedFinalcut(resFinalcuts[index].id));
            }
          }
        });
      dispatch(fetchStorageSize());
    } finally {
      dispatch(setRemovingInstance(null));
      setRemoving(false);
    }
  };

  const onRequestClose = () => {
    if (instanceType === 'finalcut') {
      dispatch(
        setRemovingInstance(selectedFinalcut ? { instance: 'finalcut', id: selectedFinalcut, modalOpen: false } : null)
      );
    } else {
      dispatch(setRemovingInstance(removingInstance ? { ...removingInstance, modalOpen: false } : null));
    }
  };

  const onSubmit = () => {
    switch (instanceType) {
      case 'folder':
        handleRemoveFolder();
        break;
      case 'finalcut':
        handleRemoveFinalcut();
        break;
      case 'file':
        handleRemoveFile();
        break;
      case 'project':
        handleRemoveProject();
        break;
      case 'story':
        handleRemoveStory();
        break;
    }
  };

  return (
    <Modal
      className={styles.projectDeletionsModal}
      onRequestClose={onRequestClose}
      isOpen={!!removingInstance?.modalOpen}
    >
      <h3>{modalTitle}</h3>
      {instanceName && <p className={styles.projectDeletionsModal__Name}>{instanceName}</p>}
      <div className={styles.projectDeletionsModal__Footer}>
        <Button outlined onClick={onRequestClose}>
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={removing}>
          {removing ? <Loader className={styles.projectDeletionsModal__Loader} /> : 'Confirm'}
        </Button>
      </div>
    </Modal>
  );
};
